import { useState, useEffect } from "react";
import { NavLink, Navigate, Route, Routes, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import {
  LayoutDashboard, MessageSquare, FileText, ShoppingCart, Users, Mail,
  Settings, LogOut, ShieldCheck, Server, CreditCard, Check, X, Send, Star, Plus,
  Quote, Receipt, LifeBuoy, BookOpen,
} from "lucide-react";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import AdminInvoices from "@/pages/admin/AdminInvoices";
import AdminTickets from "@/pages/admin/AdminTickets";
import AdminBlog from "@/pages/admin/AdminBlog";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <div className="pt-24 text-center text-sm text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

const navItems = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/contact-messages", label: "Contact Messages", icon: MessageSquare },
  { to: "/admin/quotes", label: "Quote Requests", icon: FileText },
  { to: "/admin/orders", label: "Orders & Intents", icon: ShoppingCart },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/invoices", label: "Invoices", icon: Receipt },
  { to: "/admin/tickets", label: "Support Tickets", icon: LifeBuoy },
  { to: "/admin/testimonials", label: "Testimonials", icon: Quote },
  { to: "/admin/blog", label: "Blog", icon: BookOpen },
  { to: "/admin/custom-quotes", label: "Custom Quotes", icon: CreditCard },
  { to: "/admin/newsletter", label: "Newsletter", icon: Mail },
  { to: "/admin/config", label: "Configuration", icon: Settings },
];

function Shell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const onLogout = async () => {
    await logout();
    toast.success("Signed out");
    nav("/admin/login");
  };
  return (
    <div className="min-h-[calc(100vh-4rem)] grid md:grid-cols-[260px_1fr]">
      <aside className="border-r border-black/5 dark:border-white/10 bg-white dark:bg-[#0d1a35]/40 p-4 md:sticky md:top-16 md:h-[calc(100vh-4rem)]">
        <div className="flex items-center gap-2 mb-6">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-gradient-to-br from-[#1565FF] to-[#4FC3F7] text-white">
            <ShieldCheck className="h-4 w-4" />
          </span>
          <div>
            <div className="text-sm font-semibold tracking-tight">Admin</div>
            <div className="text-xs text-muted-foreground truncate max-w-[160px]">{user?.email}</div>
          </div>
        </div>
        <nav className="space-y-1">
          {navItems.map((i) => (
            <NavLink
              key={i.to}
              to={i.to}
              end={i.end}
              data-testid={`admin-nav-${i.label.toLowerCase().replace(/\s/g,'-')}`}
              className={({ isActive }) =>
                `flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition ${
                  isActive
                    ? "bg-[#1565FF] text-white"
                    : "text-foreground/80 hover:bg-black/5 dark:hover:bg-white/5"
                }`
              }
            >
              <i.icon className="h-4 w-4" /> {i.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-6 pt-4 border-t border-black/5 dark:border-white/10 space-y-1">
          <Link to="/admin/change-password" className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground/80 hover:bg-black/5 dark:hover:bg-white/5" data-testid="admin-nav-change-password">
            <Settings className="h-4 w-4"/> Change Password
          </Link>
          <button onClick={onLogout} className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-destructive hover:bg-destructive/10" data-testid="admin-logout">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>
      <main className="p-6 md:p-10">{children}</main>
    </div>
  );
}

function StatCard({ label, value, hint }) {
  return (
    <div className="card-elevated p-6">
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-2 text-3xl font-semibold tracking-tighter">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function Dashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setData(r.data)); }, []);
  const s = data?.stats || {};
  return (
    <>
      <SEO title="Admin Dashboard — Tech Alpha SSL" />
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Live business metrics.</p>
        </div>
      </div>
      <div className="mt-8 grid gap-4 md:grid-cols-4 lg:grid-cols-4">
        <StatCard label="Contact Messages" value={s.contact_messages ?? "—"} hint={`+${s.contact_messages_week ?? 0} this week`} />
        <StatCard label="Quote Requests" value={s.quotes ?? "—"} hint={`+${s.quotes_week ?? 0} this week`} />
        <StatCard label="Purchase Intents" value={s.purchase_intents ?? "—"} hint={`+${s.purchase_intents_week ?? 0} this week`} />
        <StatCard label="Newsletter" value={s.newsletter_subscribers ?? "—"} />
        <StatCard label="Customers" value={s.customers ?? "—"} />
        <StatCard label="Invoices" value={s.invoices ?? "—"} />
        <StatCard label="Open Tickets" value={s.open_tickets ?? "—"} />
      </div>
      <div className="mt-10">
        <h2 className="text-lg font-semibold tracking-tight">Recent purchase intents</h2>
        <div className="mt-4 card-elevated overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-black/[0.02] dark:bg-white/[0.02] text-left">
              <tr>
                <Th>Plan</Th><Th>Price</Th><Th>Email</Th><Th>When</Th>
              </tr>
            </thead>
            <tbody>
              {(data?.recent_intents || []).map((i) => (
                <tr key={i.id} className="border-t border-black/5 dark:border-white/10">
                  <Td>{i.plan_name}</Td>
                  <Td>${Number(i.price || 0).toFixed(2)}</Td>
                  <Td className="text-muted-foreground">{i.email || "—"}</Td>
                  <Td className="text-muted-foreground">{new Date(i.created_at).toLocaleString()}</Td>
                </tr>
              ))}
              {(!data?.recent_intents || data.recent_intents.length === 0) && (
                <tr><Td colSpan={4} className="text-center text-muted-foreground py-8">No purchase intents yet.</Td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 font-medium">{children}</th>;
const Td = ({ children, className = "", colSpan }) => <td colSpan={colSpan} className={`px-4 py-3 ${className}`}>{children}</td>;

function DataTable({ title, endpoint, columns, testId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(endpoint).then((r) => setRows(r.data)); }, [endpoint]);
  return (
    <>
      <SEO title={`${title} — Admin`} />
      <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
      <div className="mt-6 card-elevated overflow-hidden" data-testid={testId}>
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-black/[0.02] dark:bg-white/[0.02] text-left">
            <tr>{columns.map((c) => <Th key={c.key}>{c.label}</Th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-black/5 dark:border-white/10 align-top">
                {columns.map((c) => <Td key={c.key} className={c.className || ""}>{c.render ? c.render(r) : (r[c.key] ?? "—")}</Td>)}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><Td colSpan={columns.length} className="text-center text-muted-foreground py-8">No records yet.</Td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </>
  );
}

function ContactMessages() {
  return (
    <DataTable
      title="Contact Messages"
      endpoint="/admin/contact-messages"
      testId="admin-contact-table"
      columns={[
        { key: "name", label: "Name" },
        { key: "email", label: "Email", className: "text-muted-foreground" },
        { key: "subject", label: "Subject" },
        { key: "message", label: "Message", render: (r) => <div className="max-w-md whitespace-pre-wrap">{r.message}</div> },
        { key: "created_at", label: "When", render: (r) => <span className="text-muted-foreground text-xs">{new Date(r.created_at).toLocaleString()}</span> },
      ]}
    />
  );
}

function Quotes() {
  return (
    <DataTable
      title="Quote Requests"
      endpoint="/admin/quotes"
      testId="admin-quotes-table"
      columns={[
        { key: "name", label: "Name" },
        { key: "email", label: "Email", className: "text-muted-foreground" },
        { key: "service", label: "Service" },
        { key: "budget", label: "Budget" },
        { key: "timeline", label: "Timeline" },
        { key: "details", label: "Details", render: (r) => <div className="max-w-md whitespace-pre-wrap">{r.details}</div> },
        { key: "created_at", label: "When", render: (r) => <span className="text-muted-foreground text-xs">{new Date(r.created_at).toLocaleString()}</span> },
      ]}
    />
  );
}

function Orders() {
  return (
    <DataTable
      title="Purchase Intents"
      endpoint="/admin/purchase-intents"
      testId="admin-orders-table"
      columns={[
        { key: "plan_name", label: "Plan" },
        { key: "price", label: "Price", render: (r) => `$${Number(r.price || 0).toFixed(2)}` },
        { key: "email", label: "Email", className: "text-muted-foreground" },
        { key: "ip", label: "IP", className: "text-muted-foreground" },
        { key: "created_at", label: "When", render: (r) => <span className="text-muted-foreground text-xs">{new Date(r.created_at).toLocaleString()}</span> },
      ]}
    />
  );
}

function Customers() {
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/admin/contact-messages"), api.get("/admin/quotes")]).then(([c, q]) => {
      setContacts(c.data); setQuotes(q.data);
    });
  }, []);
  const byEmail = new Map();
  [...contacts.map(c => ({ ...c, source: "contact" })), ...quotes.map(q => ({ ...q, source: "quote" }))].forEach((r) => {
    const e = (r.email || "").toLowerCase();
    if (!e) return;
    if (!byEmail.has(e)) byEmail.set(e, { email: e, name: r.name, company: r.company, contacts: 0, quotes: 0, last: r.created_at });
    const entry = byEmail.get(e);
    if (r.source === "contact") entry.contacts++;
    else entry.quotes++;
    if (r.company) entry.company = r.company;
    if (r.created_at > entry.last) entry.last = r.created_at;
  });
  const rows = Array.from(byEmail.values()).sort((a, b) => (b.last || "").localeCompare(a.last || ""));
  return (
    <>
      <SEO title="Customers — Admin" />
      <h1 className="text-2xl font-semibold tracking-tight">Customers</h1>
      <p className="text-sm text-muted-foreground mt-1">Unified view of contacts and quote requesters.</p>
      <div className="mt-6 card-elevated overflow-hidden" data-testid="admin-customers-table">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-black/[0.02] dark:bg-white/[0.02] text-left">
            <tr><Th>Email</Th><Th>Name</Th><Th>Company</Th><Th>Contacts</Th><Th>Quotes</Th><Th>Last activity</Th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.email} className="border-t border-black/5 dark:border-white/10">
                <Td>{r.email}</Td>
                <Td>{r.name || "—"}</Td>
                <Td>{r.company || "—"}</Td>
                <Td>{r.contacts}</Td>
                <Td>{r.quotes}</Td>
                <Td className="text-muted-foreground text-xs">{r.last ? new Date(r.last).toLocaleString() : "—"}</Td>
              </tr>
            ))}
            {rows.length === 0 && (<tr><Td colSpan={6} className="text-center text-muted-foreground py-8">No customers yet.</Td></tr>)}
          </tbody>
        </table>
        </div>
      </div>
    </>
  );
}

function CustomQuotes() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ customer_name: "", customer_email: "", description: "", amount: 0, status: "draft" });
  const [loading, setLoading] = useState(false);
  const load = () => api.get("/admin/custom-quotes").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/custom-quotes", { ...form, amount: parseFloat(form.amount) });
      toast.success("Custom quote created");
      setForm({ customer_name: "", customer_email: "", description: "", amount: 0, status: "draft" });
      load();
    } catch { toast.error("Failed to create"); }
    finally { setLoading(false); }
  };
  return (
    <>
      <SEO title="Custom Quotes — Admin" />
      <h1 className="text-2xl font-semibold tracking-tight">Custom Quotes</h1>
      <p className="text-sm text-muted-foreground mt-1">Create bespoke pricing for enterprise deals.</p>
      <form onSubmit={submit} className="mt-6 card-elevated p-6 grid gap-3 md:grid-cols-2" data-testid="custom-quote-form">
        <input required placeholder="Customer name" value={form.customer_name} onChange={(e)=>setForm({...form,customer_name:e.target.value})} className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" data-testid="cq-name" />
        <input required type="email" placeholder="Customer email" value={form.customer_email} onChange={(e)=>setForm({...form,customer_email:e.target.value})} className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" data-testid="cq-email" />
        <input required type="number" step="0.01" placeholder="Amount (USD)" value={form.amount} onChange={(e)=>setForm({...form,amount:e.target.value})} className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" data-testid="cq-amount" />
        <select value={form.status} onChange={(e)=>setForm({...form,status:e.target.value})} className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" data-testid="cq-status">
          <option value="draft">Draft</option><option value="sent">Sent</option><option value="accepted">Accepted</option><option value="declined">Declined</option>
        </select>
        <textarea required placeholder="Scope / description" rows={3} value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} className="md:col-span-2 rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" data-testid="cq-desc" />
        <button disabled={loading} className="btn-primary md:col-span-2 md:justify-self-start !py-2 !px-4 !text-sm" data-testid="cq-submit"><Plus className="h-4 w-4"/> Create quote</button>
      </form>

      <div className="mt-6 card-elevated overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-black/[0.02] dark:bg-white/[0.02] text-left">
            <tr><Th>Customer</Th><Th>Email</Th><Th>Amount</Th><Th>Status</Th><Th>Description</Th><Th>When</Th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-black/5 dark:border-white/10">
                <Td>{r.customer_name}</Td>
                <Td className="text-muted-foreground">{r.customer_email}</Td>
                <Td>${Number(r.amount || 0).toFixed(2)}</Td>
                <Td><span className="text-xs px-2 py-1 rounded-full bg-[#1565FF]/10 text-[#1565FF]">{r.status}</span></Td>
                <Td className="max-w-md">{r.description}</Td>
                <Td className="text-muted-foreground text-xs">{new Date(r.created_at).toLocaleString()}</Td>
              </tr>
            ))}
            {rows.length === 0 && (<tr><Td colSpan={6} className="text-center text-muted-foreground py-8">No custom quotes yet.</Td></tr>)}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Newsletter() {
  return (
    <DataTable
      title="Newsletter Subscribers"
      endpoint="/admin/newsletter"
      testId="admin-newsletter-table"
      columns={[
        { key: "email", label: "Email" },
        { key: "created_at", label: "Subscribed", render: (r) => new Date(r.created_at).toLocaleString() },
      ]}
    />
  );
}

function ConfigStatus() {
  const [status, setStatus] = useState(null);
  const load = () => api.get("/admin/config-status").then((r) => setStatus(r.data));
  useEffect(() => { load(); }, []);
  const testEmail = async () => {
    try { await api.post("/admin/test-email"); toast.success("Test email sent to support address."); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const Row = ({ label, ok, hint }) => (
    <div className="flex items-center justify-between py-3 border-b border-black/5 dark:border-white/10 last:border-0">
      <div>
        <div className="text-sm font-medium">{label}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </div>
      {ok ? (
        <span className="inline-flex items-center gap-1 text-xs rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-2 py-1"><Check className="h-3 w-3"/> Configured</span>
      ) : (
        <span className="inline-flex items-center gap-1 text-xs rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 px-2 py-1"><X className="h-3 w-3"/> Not configured</span>
      )}
    </div>
  );
  return (
    <>
      <SEO title="Configuration Status — Admin" />
      <h1 className="text-2xl font-semibold tracking-tight">Configuration Status</h1>
      <p className="text-sm text-muted-foreground mt-1">Verify integrations before going live.</p>

      {!status ? <p className="mt-6 text-muted-foreground">Loading…</p> : (
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <div className="card-elevated p-6">
            <div className="flex items-center gap-2 mb-2">
              <Send className="h-5 w-5 text-[#1565FF] dark:text-[#4FC3F7]" />
              <h2 className="text-base font-semibold tracking-tight">SMTP Email</h2>
            </div>
            <Row label="SMTP configured" ok={status.smtp.configured} hint={status.smtp.host ? `${status.smtp.host}:${status.smtp.port}` : "Add SMTP_HOST/USER/PASSWORD/FROM in backend/.env"} />
            <Row label="Username set" ok={status.smtp.user_set} />
            <Row label="Password set" ok={status.smtp.password_set} />
            <button onClick={testEmail} disabled={!status.smtp.configured} className="btn-primary mt-4 !py-2 !px-4 !text-sm disabled:opacity-50" data-testid="config-test-email">
              Send test email
            </button>
          </div>

          <div className="card-elevated p-6">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="h-5 w-5 text-[#1565FF] dark:text-[#4FC3F7]" />
              <h2 className="text-base font-semibold tracking-tight">PayPal Hosted Payments</h2>
            </div>
            <Row label="PayPal redirect URL" ok={status.paypal.configured} hint={status.paypal.hosted_url} />
            <p className="mt-3 text-xs text-muted-foreground">Mode: {status.paypal.mode}. Every checkout button redirects the current tab to the hosted PayPal page.</p>
          </div>

          <div className="card-elevated p-6">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="h-5 w-5 text-[#1565FF] dark:text-[#4FC3F7]" />
              <h2 className="text-base font-semibold tracking-tight">Authentication</h2>
            </div>
            <Row label="JWT secret set" ok={status.jwt.configured} />
          </div>

          <div className="card-elevated p-6">
            <div className="flex items-center gap-2 mb-2">
              <Server className="h-5 w-5 text-[#1565FF] dark:text-[#4FC3F7]" />
              <h2 className="text-base font-semibold tracking-tight">Database</h2>
            </div>
            <Row label="MongoDB connected" ok={status.mongo.configured} hint={`Database: ${status.mongo.db_name}`} />
          </div>
        </div>
      )}
    </>
  );
}

function ChangePassword() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmVal, setConfirmVal] = useState("");
  const [loading, setLoading] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const { refresh, user } = useAuth();
  const nav = useNavigate();

  const submitPw = async (e) => {
    e.preventDefault();
    if (next !== confirmVal) return toast.error("Passwords do not match");
    if (next.length < 8) return toast.error("Password must be at least 8 characters");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      await refresh();
      nav("/admin");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/update-email", { new_email: newEmail });
      toast.success("Admin email updated");
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  return (
    <>
      <SEO title="Change Password — Admin" />
      <h1 className="text-2xl font-semibold tracking-tight">Account Settings</h1>
      {user?.must_change_password && (
        <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-300 p-4 text-sm" data-testid="must-change-notice">
          You are using the initial password. Please set a new password to secure your account.
        </div>
      )}
      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <form onSubmit={submitPw} className="card-elevated p-6 space-y-3" data-testid="change-pw-form">
          <h2 className="text-lg font-semibold tracking-tight">Change password</h2>
          <input type="password" required placeholder="Current password" value={current} onChange={(e)=>setCurrent(e.target.value)} className="w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm" data-testid="cp-current" />
          <input type="password" required placeholder="New password" value={next} onChange={(e)=>setNext(e.target.value)} className="w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm" data-testid="cp-new" />
          <input type="password" required placeholder="Confirm new password" value={confirmVal} onChange={(e)=>setConfirmVal(e.target.value)} className="w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm" data-testid="cp-confirm" />
          <button disabled={loading} className="btn-primary !py-2 !px-4 !text-sm" data-testid="cp-submit">Update password</button>
        </form>
        <form onSubmit={submitEmail} className="card-elevated p-6 space-y-3" data-testid="change-email-form">
          <h2 className="text-lg font-semibold tracking-tight">Change admin email</h2>
          <p className="text-xs text-muted-foreground">Current: {user?.email}</p>
          <input type="email" required placeholder="New admin email" value={newEmail} onChange={(e)=>setNewEmail(e.target.value)} className="w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm" data-testid="ce-email" />
          <button disabled={loading} className="btn-ghost !py-2 !px-4 !text-sm" data-testid="ce-submit">Update email</button>
        </form>
      </div>
    </>
  );
}

export default function AdminApp() {
  return (
    <Protected>
      <Shell>
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="contact-messages" element={<ContactMessages />} />
          <Route path="quotes" element={<Quotes />} />
          <Route path="orders" element={<Orders />} />
          <Route path="customers" element={<Customers />} />
          <Route path="invoices" element={<AdminInvoices />} />
          <Route path="tickets" element={<AdminTickets />} />
          <Route path="testimonials" element={<AdminTestimonials />} />
          <Route path="blog" element={<AdminBlog />} />
          <Route path="custom-quotes" element={<CustomQuotes />} />
          <Route path="newsletter" element={<Newsletter />} />
          <Route path="config" element={<ConfigStatus />} />
          <Route path="change-password" element={<ChangePassword />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </Shell>
    </Protected>
  );
}
